import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import type { KycStatus } from '@shared';
import { PortalShell, type NavItem } from '../../components/PortalShell';
import { useAuth } from '../../lib/auth-context';
import { apiGet } from '../../lib/api';

// Compliance sidebar badge poller. Polls GET /api/portal/kyc/status every 60s
// (clients change state slowly). Surfaces a numeric badge = count of rejected
// docs when the rollup is `rejected`, and a boolean "dot" flag when the rollup
// is `in_review`. Silent on failure — the badge is best-effort.
function useKycBadge(): { rejectedCount: number; inReview: boolean } {
  const [rejectedCount, setRejectedCount] = useState(0);
  const [inReview, setInReview] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = () => {
      apiGet<{ kyc: KycStatus; by_category: Record<string, { state: string }> }>('/api/portal/kyc/status')
        .then((r) => {
          if (cancelled) return;
          const rejected = Object.values(r.by_category).filter((e) => e.state === 'rejected').length;
          setRejectedCount(r.kyc === 'rejected' ? rejected : 0);
          setInReview(r.kyc === 'in_review');
        })
        .catch(() => { /* silent — badge is best-effort */ });
    };
    fetchStatus();
    const t = window.setInterval(fetchStatus, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  return { rejectedCount, inReview };
}

// Nav-badge count poller. Fetched once on layout mount + refreshed every 60s
// so freshly-arrived items light up without a hard refresh. Soft-fails to 0
// if the endpoint is unreachable. Used for both notifications and messages.
function useUnreadCount(path: string): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      apiGet<{ count: number }>(path)
        .then((r) => { if (!cancelled) setCount(r.count); })
        .catch(() => { /* silent — badge is best-effort */ });
    };
    fetchCount();
    const t = window.setInterval(fetchCount, 60_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, [path]);
  return count;
}

export function PortalLayout() {
  const { user } = useAuth();
  const unread = useUnreadCount('/api/notifications/unread-count');
  const unreadMessages = useUnreadCount('/api/messages/unread-count');
  const kycBadge = useKycBadge();
  const initials = (user?.initials ?? user?.name?.split(/\s+/).slice(0, 2).map((s) => s[0]).join('') ?? 'U').toUpperCase();
  const who = user?.name ?? 'Authorised contact';
  const whoSub = user?.contact_role
    ? `${user.contact_role} · Body corporate`
    : 'Body corporate client';

  const items: NavItem[] = useMemo(() => ([
    { to: '/portal',               label: 'Dashboard',     icon: '◰', end: true },
    { to: '/portal/requests',      label: 'My briefs',     icon: '◧', end: true },
    { to: '/portal/requests/new',  label: 'New brief',     icon: '+' },
    { to: '/portal/proposals',     label: 'Proposals',     icon: '▤' },
    { to: '/portal/invoices',      label: 'Invoices',      icon: '£' },
    { to: '/portal/documents',     label: 'Documents',     icon: '▦' },
    { to: '/portal/compliance',    label: 'Compliance',    icon: '⛨',
      badge: kycBadge.rejectedCount > 0 ? kycBadge.rejectedCount : undefined,
      dot: kycBadge.inReview },
    { to: '/portal/messages',      label: 'Messages',      icon: '✉', badge: unreadMessages > 0 ? unreadMessages : undefined },
    { to: '/portal/notifications', label: 'Notifications', icon: '⚑', badge: unread > 0 ? unread : undefined },
    { to: '/portal/support',       label: 'Support',       icon: '?' },
    { to: '/portal/settings',      label: 'Settings',      icon: '⛭' },
  ]), [unread, unreadMessages, kycBadge.rejectedCount, kycBadge.inReview]);

  return (
    <PortalShell
      who={who}
      whoSub={whoSub}
      initials={initials}
      items={items}
      signOutTo="/sign-in"
    >
      <Outlet />
    </PortalShell>
  );
}
